import {
  Mic,
  Headphones,
  Radio,
  BookOpen,
  Users,
  Lightbulb,
  Heart,
  Star,
  ArrowRight,
  Volume2,
  Award,
  Globe,
  Sparkles,
} from "lucide-react";

import { ACCENT, ACCENT_LIGHT } from "../constants/theme";
import Btn from "../components/Button";
import Card from "../components/Card";
import SectionLabel from "../components/SectionLabel";

export default function Home({ setPage }) {
  const services = [
    {
      icon: <Volume2 size={26} color={ACCENT} />,
      title: "Immersive Audio Stories",
      desc: "We craft emotional, character-led audio stories with cinematic sound design that make listeners feel part of the world.",
    },
    {
      icon: <Radio size={26} color={ACCENT} />,
      title: "Podcast Production",
      desc: "From concept to final episode, we create meaningful podcasts that share fresh ideas, real voices, and powerful conversations.",
    },
    {
      icon: <Headphones size={26} color={ACCENT} />,
      title: "Sound Design",
      desc: "We build rich soundscapes, background scores, effects, and audio moods that make every production feel polished and memorable.",
    },
    {
      icon: <Mic size={26} color={ACCENT} />,
      title: "Voice Over & Narration",
      desc: "We support voice artists with expressive narration for stories, podcasts, learning content, commercials, and documentaries.",
    },
    {
      icon: <BookOpen size={26} color={ACCENT} />,
      title: "Creative Training Programs",
      desc: "We offer practical training in audio production, script writing, editing, voice-over, sound design, and inclusive media creation.",
    },
  ];

  const whyItems = [
    {
      icon: <Heart size={22} color={ACCENT} />,
      title: "Inclusive Workspace",
      desc: "Disabled and non-disabled creators work together with equal respect, equal opportunity, and shared creative ownership.",
    },
    {
      icon: <Lightbulb size={22} color={ACCENT} />,
      title: "Fresh Creative Vision",
      desc: "We believe different lived experiences create stronger stories, deeper emotions, and more meaningful productions.",
    },
    {
      icon: <Award size={22} color={ACCENT} />,
      title: "Skill-Based Growth",
      desc: "Our training programs help creators build practical skills, confidence, and a strong foundation for the creative industry.",
    },
    {
      icon: <Users size={22} color={ACCENT} />,
      title: "Real Collaboration",
      desc: "Every project is shaped through teamwork, shared ideas, and a process where every creator’s voice is valued.",
    },
    {
      icon: <Globe size={22} color={ACCENT} />,
      title: "Future of Audio",
      desc: "We are building next-generation audio experiences through podcasts, immersive stories, sound design, and voice talent.",
    },
    {
      icon: <Star size={22} color={ACCENT} />,
      title: "A Stage for Talent",
      desc: "Nexus Studio gives creators a platform to showcase their talent, share their voice, and grow professionally.",
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section style={{ background: "#fff", padding: "96px 24px 80px" }}>
        <div style={{ maxWidth: 950, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: ACCENT_LIGHT,
              borderRadius: 20,
              padding: "6px 18px",
              marginBottom: 28,
            }}
          >
            <Sparkles size={14} color={ACCENT} />
            <span
              style={{
                color: ACCENT,
                fontWeight: 700,
                fontSize: 12.5,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              India’s Inclusive Audio & Creative Studio
            </span>
          </div>

          <h1
            style={{
              fontSize: "clamp(44px, 8vw, 80px)",
              fontWeight: 900,
              color: "#0f172a",
              lineHeight: 1.05,
              letterSpacing: "-2px",
              marginBottom: 18,
            }}
          >
            Nexus<span style={{ color: ACCENT }}>Studio</span>
          </h1>

          <p
            style={{
              fontSize: "clamp(18px, 3vw, 26px)",
              fontWeight: 600,
              color: "#475569",
              marginBottom: 20,
            }}
          >
            Vision Beyond Inclusion.
          </p>

          <p
            style={{
              fontSize: 17,
              color: "#64748b",
              lineHeight: 1.75,
              maxWidth: 720,
              margin: "0 auto 40px",
            }}
          >
            Nexus Studio is a creative platform where disabled and non-disabled
            creators come together to learn, collaborate, and produce powerful
            audio stories, podcasts, voice-over, and sound experiences.
          </p>

          <div
            style={{
              display: "flex",
              gap: 14,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Btn onClick={() => setPage("Join Us")}>
              Join Us <ArrowRight size={16} />
            </Btn>
            <Btn outline onClick={() => setPage("Training Programs")}>
              Explore Training Programs
            </Btn>
          </div>
        </div>

        {/* Stats strip */}
        <div
          style={{
            maxWidth: 860,
            margin: "72px auto 0",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 1,
            background: "#e2e8f0",
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          {[
            ["200+", "Creators Empowered"],
            ["5+", "Audio Productions"],
            ["6", "Skill Programs"],
            ["100%", "Inclusive Workspace"],
          ].map(([num, label]) => (
            <div
              key={label}
              style={{
                background: "#fff",
                padding: "28px 24px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontWeight: 900,
                  fontSize: 32,
                  color: ACCENT,
                  marginBottom: 4,
                }}
              >
                {num}
              </p>
              <p style={{ color: "#64748b", fontSize: 13.5, fontWeight: 500 }}>
                {label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* What We Do */}
      <section style={{ background: "#f8fafc", padding: "88px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <SectionLabel>What We Do</SectionLabel>
            <h2
              style={{
                fontSize: "clamp(28px, 4vw, 44px)",
                fontWeight: 800,
                color: "#0f172a",
              }}
            >
              Audio Stories, Podcasts & Creative Training
            </h2>
            <p
              style={{
                color: "#64748b",
                fontSize: 16,
                maxWidth: 620,
                margin: "12px auto 0",
              }}
            >
              We help creators turn ideas into professional audio experiences
              through storytelling, production, sound design, voice-over, and
              hands-on training.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 24,
            }}
          >
            {services.map((s) => (
              <Card key={s.title}>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    background: ACCENT_LIGHT,
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 20,
                  }}
                >
                  {s.icon}
                </div>
                <h3
                  style={{
                    fontWeight: 700,
                    fontSize: 17,
                    color: "#0f172a",
                    marginBottom: 10,
                  }}
                >
                  {s.title}
                </h3>
                <p style={{ color: "#64748b", fontSize: 14.5, lineHeight: 1.65 }}>
                  {s.desc}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Nexus */}
      <section style={{ background: "#fff", padding: "88px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <SectionLabel>Why Nexus?</SectionLabel>
            <h2
              style={{
                fontSize: "clamp(28px, 4vw, 44px)",
                fontWeight: 800,
                color: "#0f172a",
              }}
            >
              A Studio Where Every Voice Matters
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 24,
            }}
          >
            {whyItems.map((w) => (
              <div
                key={w.title}
                style={{
                  display: "flex",
                  gap: 16,
                  padding: 24,
                  borderRadius: 14,
                  border: "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    background: ACCENT_LIGHT,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {w.icon}
                </div>
                <div>
                  <h3
                    style={{
                      fontWeight: 700,
                      fontSize: 15.5,
                      color: "#0f172a",
                      marginBottom: 6,
                    }}
                  >
                    {w.title}
                  </h3>
                  <p
                    style={{
                      color: "#64748b",
                      fontSize: 13.5,
                      lineHeight: 1.6,
                    }}
                  >
                    {w.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: ACCENT_LIGHT, padding: "88px 24px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <SectionLabel>Join The Movement</SectionLabel>
          <h2
            style={{
              fontSize: "clamp(26px, 4vw, 42px)",
              fontWeight: 800,
              color: "#0f172a",
              marginBottom: 16,
            }}
          >
            Create. Learn. Collaborate.
          </h2>
          <p
            style={{
              color: "#475569",
              fontSize: 16,
              lineHeight: 1.75,
              marginBottom: 36,
            }}
          >
            Whether you want to join as a creator, voice artist, trainer,
            editor, writer, or learner, Nexus Studio gives you the space,
            support, and stage to grow.
          </p>

          <div
            style={{
              display: "flex",
              gap: 14,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Btn onClick={() => setPage("Join Us")}>
              Join Us <ArrowRight size={16} />
            </Btn>
            <Btn outline onClick={() => setPage("Training Programs")}>
              Training Programs
            </Btn>
          </div>
        </div>
      </section>
    </div>
  );
}